/**
 * Creation and edition of a made-to-measure element (button, lining, collar...)
 * with its main picture and, for the types drawn as a layer, its sketch picture
 * (the "black" picture of the API), both measured before upload.
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DMS_ELEMENT_TYPES } from '@/shared/constants';
import { ApiError } from '@/shared/api/client';
import { Dialog } from '@/shared/ui/Dialog';
import { PictureField } from '@/shared/ui/PictureField';
import { hasPicture, usePicture } from '@/shared/ui/use-picture';
import { createDmsItem, updateDmsItem, syncDmsItemPicture } from '../api';
import {
  CODE_PART_MESSAGES,
  codePartField,
  elementDataSchema,
  elementFormValues,
  elementPictureUrl,
  elementSketchUrl,
  formToElementData,
  hasSketchPicture,
  MAIN_PICTURE_SIZE,
  SKETCH_PICTURE_SIZE,
  validateElementForm,
  type DmsElement,
  type ElementFormErrors,
  type ElementFormValues,
} from './element-form';
import { Select } from '@/shared/ui/Select';
import { optionsOf } from '@/shared/ui/select-options';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface ElementDialogProps {
  item: DmsElement | null;
  /** Type filter of the list, preset on a new element. */
  type: string | null;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

const apiMessage = (error: unknown, fallback: string): string => {
  if (!(error instanceof ApiError)) return fallback;
  if (typeof error.body === 'string' && error.body) return error.body;
  if (
    error.body &&
    typeof error.body === 'object' &&
    'message' in error.body &&
    typeof error.body.message === 'string' &&
    error.body.message
  ) {
    return error.body.message;
  }
  return fallback;
};

const DIMENSION_ERROR_MESSAGE =
  'The size of the photo is incorrect, please correct the file.';

const CODE_PART_LABELS = {
  buttonStyleCodePart: { label: 'Button Code', placeholder: 'Button Code' },
  shirtButtonStyleCodePart: {
    label: 'Button Code',
    placeholder: 'Shirt Button Code',
  },
  liningStyleCodePart: { label: 'Lining Code', placeholder: 'Lining Code' },
} as const;

export function ElementDialog({
  item,
  type,
  onClose,
}: Readonly<ElementDialogProps>) {
  const creating = !item;
  const [values, setValues] = useState<ElementFormValues>(() =>
    elementFormValues(item, type),
  );
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<ElementFormErrors>({});
  const [dirty, setDirty] = useState<Set<keyof ElementFormValues>>(
    () => new Set(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const withSketch = hasSketchPicture(values.type);
  const picture = usePicture({
    expected: withSketch ? MAIN_PICTURE_SIZE : null,
    dimensionWarningOnly: withSketch,
    dimensionErrorMessage: DIMENSION_ERROR_MESSAGE,
    touchOnServerError: true,
  });
  const sketch = usePicture({
    expected: withSketch ? SKETCH_PICTURE_SIZE : null,
    dimensionErrorMessage: DIMENSION_ERROR_MESSAGE,
    touchOnServerError: true,
  });
  const mainUrl = elementPictureUrl(item);
  const sketchUrl = elementSketchUrl(item);

  const set = <K extends keyof ElementFormValues>(
    key: K,
    value: ElementFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => new Set(prev).add(key));
    if (key === 'type') {
      // The expected dimensions follow the type: chosen files are measured again.
      const sketchNext = hasSketchPicture(String(value));
      picture.revalidate(sketchNext ? MAIN_PICTURE_SIZE : null);
      sketch.revalidate(sketchNext ? SKETCH_PICTURE_SIZE : null);
    }
  };
  const liveErrors = validateElementForm(values);
  const invalidClass = (key: keyof ElementFormValues) =>
    dirty.has(key) && liveErrors[key] ? 'invalid' : '';
  const fieldError = (key: keyof ElementFormValues) => submitted && errors[key];
  const codePart = codePartField(values.type);
  const testId = (key: string) => `mtm-element-form-${kebabCase(key)}`;

  /** A touched field reaches the API, the others keep the stored path. */
  const handlePicture = (
    id: string,
    code: string,
    pictureType: 'main' | 'black',
  ): Promise<string | null> => {
    if (pictureType === 'black' && !withSketch)
      return Promise.resolve(item?.data.blackPicture ?? null);
    const field = pictureType === 'black' ? sketch : picture;
    const stored =
      pictureType === 'black' ? item?.data.blackPicture : item?.data.picture;
    return field.touched
      ? syncDmsItemPicture(id, pictureType, code, field.file)
      : Promise.resolve(stored ?? null);
  };
  const handlePictures = (id: string, code: string) =>
    Promise.all([
      handlePicture(id, code, 'main'),
      handlePicture(id, code, 'black'),
    ]);

  /** Both pictures once the type is drawn as a layer. */
  const missingPictures = (): boolean =>
    withSketch &&
    (!hasPicture(picture, item?.data.picture) ||
      !hasPicture(sketch, item ? sketchUrl : null));

  const save = useMutation({
    mutationFn: async () => {
      const data = formToElementData(values, Date.now());
      if (creating) {
        let created: DmsElement;
        try {
          created = await createDmsItem(
            { type: 'element', data },
            elementDataSchema,
          );
        } catch (error) {
          throw new Error(apiMessage(error, 'Failed to create item'));
        }
        try {
          const [url, sketchPath] = await handlePictures(
            created.id,
            String(created.data?.codeBu ?? ''),
          );
          await updateDmsItem(
            created.id,
            {
              data: { ...created.data, picture: url, blackPicture: sketchPath },
            },
            elementDataSchema,
          );
        } catch (error) {
          throw new Error('Failed to upload pictures', { cause: error });
        }
        return;
      }
      let url: string | null;
      let sketchPath: string | null;
      try {
        [url, sketchPath] = await handlePictures(item.id, values.codeBu);
      } catch (error) {
        throw new Error('Failed to upload pictures', { cause: error });
      }
      try {
        await updateDmsItem(
          item.id,
          {
            type: 'element',
            data: { ...data, picture: url, blackPicture: sketchPath },
          },
          elementDataSchema,
        );
      } catch (error) {
        throw new Error(apiMessage(error, 'Failed to update item'));
      }
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    const nextErrors = validateElementForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length || missingPictures()) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const text = (
    key: keyof Pick<
      ElementFormValues,
      | 'name'
      | 'codeBu'
      | 'buttonStyleCodePart'
      | 'shirtButtonStyleCodePart'
      | 'liningStyleCodePart'
      | 'order'
      | 'description'
    >,
    label: string,
    placeholder: string,
    message: string,
    inputType: 'text' | 'number' = 'text',
  ) => (
    <div className="mb-2">
      <label className="field-label" htmlFor={`element-${key}`}>
        {label}
      </label>
      <input
        id={`element-${key}`}
        data-testid={testId(key)}
        className={`field ${invalidClass(key)}`}
        name={key}
        type={inputType}
        placeholder={placeholder}
        value={values[key]}
        onChange={(event) => set(key, event.target.value)}
      />
      <small hidden={!fieldError(key)} data-testid={`${testId(key)}-error`}>
        {message}
      </small>
    </div>
  );

  return (
    <Dialog
      title={creating ? 'Add an element' : 'Edit an element'}
      width="800px"
      onClose={() => onClose('Cancel')}
      testId="mtm-element-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row">
          <div className="col-span-6">
            {text('name', 'Element Name', 'Element name', 'Wrong name')}
            <div className="mb-2">
              <label className="field-label" htmlFor="element-type">
                Element Type
              </label>
              <div className={`field field-selector ${invalidClass('type')}`}>
                <Select
                  id="element-type"
                  name="type"
                  testId={testId('type')}
                  placeholder="--Select element type--"
                  options={optionsOf(DMS_ELEMENT_TYPES)}
                  value={values.type}
                  onChange={(type) => set('type', type)}
                />
              </div>
              <small
                hidden={!fieldError('type')}
                data-testid={`${testId('type')}-error`}
              >
                Wrong type
              </small>
            </div>
            {text('codeBu', 'Code BU', 'Code BU', 'Wrong Code BU')}
            {codePart &&
              text(
                codePart,
                CODE_PART_LABELS[codePart].label,
                CODE_PART_LABELS[codePart].placeholder,
                CODE_PART_MESSAGES[codePart],
              )}
            {text('order', 'Order', 'Order', 'Wrong order', 'number')}
            {text(
              'description',
              'Description',
              'Description',
              'Wrong description',
            )}
          </div>
          <div className="col-span-6">
            <PictureField
              id="element-picture"
              name="mainPicture"
              testId={testId('picture')}
              label="Main picture"
              picture={picture}
              serverUrl={mainUrl}
              showMessages
              required={withSketch}
              submitted={submitted}
            />
            {withSketch && (
              <PictureField
                id="element-sketch"
                name="sketchPicture"
                testId={testId('sketchPicture')}
                label="Sketch picture"
                picture={sketch}
                serverUrl={sketchUrl}
                showMessages
                required
                submitted={submitted}
              />
            )}
            <div className="mb-1 mt-4">
              <span className="field-label">Availability</span>
            </div>
            <div className="styled-input-square styled-input-single">
              <input
                type="checkbox"
                id="element-availablePermanent"
                name="availablePermanent"
                data-testid={testId('availablePermanent')}
                checked={values.availablePermanent}
                onChange={(event) =>
                  set('availablePermanent', event.target.checked)
                }
              />
              <label htmlFor="element-availablePermanent">Permanent</label>
            </div>
            <div className="styled-input-square styled-input-single">
              <input
                type="checkbox"
                id="element-availableVillaDior"
                name="availableVillaDior"
                data-testid={testId('availableVillaDior')}
                checked={values.availableVillaDior}
                onChange={(event) =>
                  set('availableVillaDior', event.target.checked)
                }
              />
              <label htmlFor="element-availableVillaDior">Villa Dior</label>
            </div>
          </div>
        </div>
        {errorMessage && (
          <div
            className="alert mb-0"
            role="alert"
            data-testid="mtm-element-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-1 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            data-testid="mtm-element-form-save-button"
            disabled={save.isPending || picture.validating || sketch.validating}
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
